import { useQuery } from '@tanstack/react-query';
import { getForceResetFlag } from 'api/services';
import { DragHandleIcon } from 'library/icons';
import { ProgressTabModal, ProgressTabProvider } from 'library/progress-tab';
import AnnouncementBanner from 'modules/shared/components/announcement-banner/AnnouncementBanner';
import EventBlocker from 'modules/shared/components/helpers/EventBlocker';
import { FeatureRolloutProvider } from 'modules/shared/features/feature-rollout';
import { Navbar, Sidebar } from 'modules/shared/features/navigation';
import { ProgressGPTDrawer } from 'modules/shared/features/progressgpt';
import AIAvatar from 'modules/shared/features/progressgpt/components/AIAvatar/AIAvatar';
import useDialog from 'modules/shared/hooks/useDialog';
import { useEffect, useRef, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import PageLoader from './components/PageLoader/PageLoader';
import useCurrentUser from './hooks/useCurrentUser';
import useSession from './hooks/useSession';
import useAnnouncementBannerData from 'modules/shared/components/hooks/useAnnouncementBannerData';
import useAuthSelector from 'modules/shared/hooks/useAuthSelector';
import ReleaseAnnouncementExperience from 'modules/shared/features/release-announcements/components/ReleaseAnnouncementExperience';

const MainLayout = () => {
    const navigate = useNavigate();
    const { user, selectedTheme } = useCurrentUser();

    const { client } = useAuthSelector();

    const getForceResetFlagQuery = useQuery({ queryKey: ['forceResetFlag'], queryFn: () => getForceResetFlag() });

    const announcementBannerQuery = useAnnouncementBannerData();

    const banner = announcementBannerQuery.announcementBanner;

    const progressGPTDialog = useDialog();

    // const resetPasswordDialog = useDialog<boolean>(false);

    const [position, setPosition] = useState({ x: 40, y: 40 });
    const [isDragging, setIsDragging] = useState(false);
    const dragRef = useRef({ startX: 0, startY: 0, initialX: 0, initialY: 0 });

    useSession();

    const handleMouseDown = (e: React.MouseEvent) => {
        setIsDragging(true);
        dragRef.current = {
            startX: e.clientX,
            startY: e.clientY,
            initialX: position.x,
            initialY: position.y,
        };
    };

    const handleMouseMove = (e: MouseEvent) => {
        if (!isDragging) return;

        const deltaX = e.clientX - dragRef.current.startX;
        const deltaY = e.clientY - dragRef.current.startY;

        setPosition({
            x: dragRef.current.initialX - deltaX,
            y: dragRef.current.initialY - deltaY,
        });
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    useEffect(() => {
        if (isDragging) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
            return () => {
                window.removeEventListener('mousemove', handleMouseMove);
                window.removeEventListener('mouseup', handleMouseUp);
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDragging, position]);

    useEffect(() => {
        if (getForceResetFlagQuery.data?.forceLogin) {
            navigate('suspicious-activity');
        }

        if (getForceResetFlagQuery.data?.microsoftSsoEnable) {
            navigate('force-microsoft-authenticate');
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getForceResetFlagQuery.data]);

    if (!user) return <PageLoader />;

    return (
        <FeatureRolloutProvider>
            {({ hasFeature }) => (
                <ProgressTabProvider>
                    <div
                        className="relative min-h-[100dvh] bg-background-color bg-no-repeat bg-fixed bg-[length:100%_100%]"
                        style={{
                            backgroundImage: selectedTheme.backgroundImage
                                ? `linear-gradient(to top, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.8)), url(${selectedTheme.backgroundImage})`
                                : undefined,
                        }}
                    >
                        <Sidebar />

                        <div className="ml-[var(--sidebar-width)]">
                            {banner?.isActive && banner?.clients.includes(client._id) && (
                                <AnnouncementBanner
                                    type={announcementBannerQuery.announcementBanner.type}
                                    message={announcementBannerQuery.announcementBanner.message}
                                />
                            )}

                            <Navbar />

                            <Outlet />
                        </div>
                    </div>

                    {/* {hasFeature('PROGRESS_GPT') && <ProgressGPTModal />} */}
                    {hasFeature('PROGRESS_GPT') && (
                        <div className="relative group">
                            {/* right-10 bottom-10 */}
                            <button
                                className="fixed  w-14 h-14 shadow-[0px_2px_32px_-4px_rgba(3,17,22,0.1)] dark:shadow-[0px_2px_32px_-4px_rgba(3,17,22,0.8)] rounded-full outline-none z-[80] mt-3"
                                onClick={progressGPTDialog.show}
                                style={{
                                    right: `${position.x}px`,
                                    bottom: `${position.y}px`,
                                }}
                            >
                                <AIAvatar size={56} animate />
                                <EventBlocker stopPropagation>
                                    <button
                                        onMouseDown={handleMouseDown}
                                        type="button"
                                        style={{
                                            cursor: isDragging ? 'grabbing' : 'grab',
                                        }}
                                        className="flex items-center justify-center w-6 h-8 absolute left-[-12.5px] top-[50%]  -translate-y-[50%] text-grayscale-500 transition-opacity opacity-0  group-hover:opacity-100 cursor-move"
                                    >
                                        <DragHandleIcon />
                                    </button>
                                </EventBlocker>
                            </button>
                        </div>
                    )}

                    <ProgressTabModal />

                    <ReleaseAnnouncementExperience />

                    <ProgressGPTDrawer show={progressGPTDialog.isOpen} onClose={progressGPTDialog.hide} />

                    {/* <ResetPasswordModal show={resetPasswordDialog.isOpen} /> */}
                </ProgressTabProvider>
            )}
        </FeatureRolloutProvider>
    );
};

export default MainLayout;
